import HomeView from '@/views/HomeView.vue';
import { reactive } from 'vue';
import type { RouteRecordRaw } from 'vue-router';

export const navigationState = reactive({
  lastNavigationType: '',
});

export enum SettingVariant {
  TECHNICAL = 1,
  BUSINESS = 2,
}

export enum SchemeListVariant {
  SCHEME = 1,
  SUPPLIER_ASSIGNMENT = 2,
}

/** List of all routes the user can navigate to.
 *  Please declare, whether authentication is enabled/disabled via the meta parameter.
 */
export const routes: RouteRecordRaw[] = [
  {
    path: '/',
    name: 'home',
    component: HomeView,
    meta: { requiresAuth: true, requiredGroups: [] },
  },
  {
    path: '/taskmanagement',
    name: 'taskmanagement',
    component: () => import('@/views/taskmanagement/TaskManagementView.vue'),
    meta: { requiresAuth: true, requiredGroups: [] },
  },
  {
    path: '/scheme',
    name: 'scheme',
    children: [
      {
        path: 'overview',
        name: 'overview',
        component: () => import('@/components/shared-components/WrapperView.vue'),
        meta: { requiresAuth: true, requiredGroups: [] },
        children: [
          {
            path: '',
            name: 'scheme-overview',
            props: { variantId: SchemeListVariant.SCHEME },
            component: () => import('@/views/scheme/SchemeListView.vue'),
          },
          {
            path: 'scheme-create',
            name: 'scheme-create',
            component: () => import('@/views/scheme/SchemeCreateView.vue'),
          },
          {
            path: 'scheme-details/:schemeId',
            name: 'scheme-details',
            component: () => import('@/views/scheme/SchemeDetailsView.vue'),
          },
        ],
      },
      {
        path: 'notice-board',
        name: 'notice-board',
        component: () => import('@/views/scheme/notification/NotificationView.vue'),
        meta: { requiresAuth: true, requiredGroups: [] },
      },
    ],
    meta: { requiresAuth: true, requiredGroups: [] },
  },
  {
    path: '/supplier-assignment',
    name: 'supplier-assignment',
    children: [
      {
        path: '',
        name: 'supplier-overview',
        props: { variantId: SchemeListVariant.SUPPLIER_ASSIGNMENT },
        component: () => import('@/views/scheme/SchemeListView.vue'),
      },
      {
        path: 'details/:schemeId',
        name: 'supplier-assignment-detail',
        props: true,
        component: () => import('@/views/supplier-assignment/SupplierAssignmentView.vue'),
      },
    ],
    meta: { requiresAuth: true, requiredGroups: [] },
  },
  {
    path: '/dispo',
    name: 'dispo',
    component: () => import('@/views/dispo/DispoView.vue'),
    meta: { requiresAuth: true, requiredGroups: [] },
  },
  {
    path: '/dispo/dispo-details/:dispoId',
    name: 'dispo-details',
    component: () => import('@/views/dispo/DispoDetailsView.vue'),
    meta: { requiresAuth: true, requiredGroups: [] },
  },
  {
    path: '/send',
    name: 'send',
    children: [
      {
        path: 'send-food',
        name: 'send-food',
        component: () => import('@/views/send/send-food/SendMonitorView.vue'),
        meta: { requiresAuth: true, requiredGroups: [] },
      },
      {
        path: 'send-non-food',
        name: 'send-non-food',
        component: () => import('@/views/send/send-non-food/SendMonitorNonFoodView.vue'),
        meta: { requiresAuth: true, requiredGroups: [] },
      },
    ],
    meta: { requiresAuth: true, requiredGroups: [] },
  },
  {
    path: '/non-food',
    name: 'non-food',
    children: [
      {
        path: 'logistic-planner',
        name: 'logistic-planner',
        component: () => import('@/components/shared-components/WrapperView.vue'),
        meta: { requiresAuth: true, requiredGroups: [] },
        children: [
          {
            path: 'dispo-weeks',
            name: 'dispo-weeks',
            component: () => import('@/views/non-food/logistic-planner/DispoWeeksView.vue'),
          },
          {
            path: 'dispo-week-details/:dispoWeekId',
            name: 'dispo-week-details',
            component: () => import('@/views/non-food/logistic-planner/DispoWeekDetailView.vue'),
            props: true,
          },
          {
            path: 'dispo-week-details/:dispoWeekId/:ianAndCountryId',
            name: 'dispo-optimization',
            component: () => import('@/views/non-food/logistic-planner/DispoOptimizationView.vue'),
            props: true,
          },
        ],
      },
      {
        path: 'changes',
        name: 'changes',
        component: () => import('@/views/non-food/change/ChangeView.vue'),
        meta: { requiresAuth: true, requiredGroups: [] },
      },
      {
        path: 'import-feedback',
        name: 'import-feedback',
        component: () => import('@/views/non-food/feedback/ImportFeedbackView.vue'),
        meta: { requiresAuth: true, requiredGroups: [] },
      },
    ],
    meta: { requiresAuth: true, requiredGroups: [] },
  },
  {
    path: '/settings',
    name: 'settings',
    component: () => import('@/views/settings/SettingsView.vue'),
    children: [
      {
        path: 'scheme-parameter-basic-data',
        name: 'scheme-parameter-basic-data',
        component: () => import('@/views/settings/scheme-parameter/BasicDataView.vue'),
        meta: { requiresAuth: true, requiredGroups: [] },
      },
      {
        path: 'scheme-parameter-basic-settings',
        name: 'scheme-parameter-basic-settings',
        component: () => import('@/views/settings/scheme-parameter/BasicSettingsView.vue'),
        meta: { requiresAuth: true, requiredGroups: [] },
      },
      {
        path: 'scheme-parameter-mail-settings',
        name: 'scheme-parameter-mail-settings',
        component: () => import('@/views/settings/scheme-parameter/MailSettingsView.vue'),
        meta: { requiresAuth: true, requiredGroups: [] },
      },
      {
        path: 'scheme-parameter-email-body',
        name: 'scheme-parameter-email-body',
        component: () => import('@/views/settings/scheme-parameter/EMailBodyView.vue'),
        meta: { requiresAuth: true, requiredGroups: [] },
      },
      {
        path: 'business-partner',
        name: 'business-partner',
        component: () => import('@/views/settings/business-partner/BusinessPartnerView.vue'),
        meta: { requiresAuth: true, requiredGroups: [] },
        children: [
          {
            path: 'business-partner-details/:businessPartnerId',
            name: 'business-partner-details',
            component: () => import('@/views/settings/business-partner/BusinessPartnerDetailsView.vue'),
          },
        ],
      },
      {
        path: 'external-warehouses',
        name: 'external-warehouses',
        component: () => import('@/views/settings/external-warehouses/ExternalWarehousesView.vue'),
        meta: { requiresAuth: true, requiredGroups: [] },
      },
      {
        path: 'non-food-dispo-weeks',
        name: 'non-food-dispo-weeks',
        component: () => import('@/views/settings/non-food/DispoWeeksView.vue'),
        meta: { requiresAuth: true, requiredGroups: [] },
      },
      {
        path: 'non-food-optimizing',
        name: 'non-food-optimizing',
        component: () => import('@/views/settings/non-food/OptimizingView.vue'),
        meta: { requiresAuth: true, requiredGroups: [] },
      },
      {
        path: 'non-food-email-body',
        name: 'non-food-email-body',
        component: () => import('@/views/settings/non-food/NonFoodEMailBodyView.vue'),
        meta: { requiresAuth: true, requiredGroups: [] },
      },
      {
        path: 'non-food-external-warehouses',
        name: 'non-food-external-warehouses',
        component: () => import('@/views/settings/non-food/ExternalWarehousesView.vue'),
        meta: { requiresAuth: true, requiredGroups: [] },
      },
    ],
    meta: { requiresAuth: true, requiredGroups: [] },
  },
  {
    path: '/system',
    name: 'system',
    component: () => import('@/views/system/SystemView.vue'),
    children: [
      {
        path: 'roles',
        name: 'roles',
        component: () => import('@/views/system/roles/RolesView.vue'),
        children: [
          {
            path: 'create-roles',
            name: 'create-roles',
            component: () => import('@/views/system/roles/RolesCreateAndEditView.vue'),
          },
          {
            path: 'role-details/:roleId',
            name: 'role-details',
            component: () => import('@/views/system/roles/RolesCreateAndEditView.vue'),
          },
        ],
        meta: { requiresAuth: true, requiredGroups: [] },
      },
      {
        path: 'permissions',
        name: 'permissions',
        component: () => import('@/views/system/permissions/PermissionsView.vue'),
        meta: { requiresAuth: true, requiredGroups: [] },
      },
      {
        path: 'reports',
        name: 'reports',
        component: () => import('@/views/system/reports/ReportsView.vue'),
        meta: { requiresAuth: true, requiredGroups: [] },
      },
      {
        path: 'news-administration',
        name: 'news-administration',
        component: () => import('@/views/system/news-administration/NewsAdministrationView.vue'),
        meta: { requiresAuth: true, requiredGroups: [] },
      },
      {
        path: 'technical-parameters',
        name: 'technical-parameters',
        props: { variantId: SettingVariant.TECHNICAL },
        component: () => import('@/views/system/parameters/ParametersView.vue'),
        meta: { requiresAuth: true, requiredGroups: [] },
      },
      {
        path: 'business-parameters',
        name: 'business-parameters',
        props: { variantId: SettingVariant.BUSINESS },
        component: () => import('@/views/system/parameters/ParametersView.vue'),
        meta: { requiresAuth: true, requiredGroups: [] },
      },
      {
        path: 'manual-calls',
        name: 'manual-calls',
        component: () => import('@/views/system/manual-calls/ManualCallsView.vue'),
        meta: { requiresAuth: true, requiredGroups: [] },
      },
      {
        path: 'loggings',
        name: 'loggings',
        component: () => import('@/views/system/loggings/LoggingsView.vue'),
        meta: { requiresAuth: true, requiredGroups: [] },
      },
      {
        path: 'job-scheduler',
        name: 'job-scheduler',
        component: () => import('@/views/system/scheduler/SchedulerView.vue'),
        meta: { requiresAuth: true, requiredGroups: [] },
      },
      {
        path: 'edi-errors',
        name: 'edi-errors',
        component: () => import('@/views/system/edi-errors/EdiErrorsView.vue'),
        meta: { requiresAuth: true, requiredGroups: [] },
      },
      {
        path: 'file-transfer-errors',
        name: 'file-transfer-errors',
        component: () => import('@/views/system/file-transfer-errors/FileTransferErrorsView.vue'),
        meta: { requiresAuth: true, requiredGroups: [] },
      },
      {
        path: 'dispo-import',
        name: 'dispo-import',
        component: () => import('@/views/system/dispo-import/DispoImportView.vue'),
        meta: { requiresAuth: true, requiredGroups: [] },
      },
    ],
    meta: { requiresAuth: true, requiredGroups: [] },
  },
  {
    /**
     * Global error page
     * In case the user is not allowed to access a certain page they will be redirected here.
     */
    path: '/error',
    name: 'Error',
    component: () => import('@/views/ErrorView.vue'),
    meta: { requiresAuth: false },
  },
  {
    /**
     * Logout page
     * When the user logged out while visiting a restricted page, they will be redirected here.
     */
    path: '/logged-out',
    name: 'loggedOut',
    component: () => import('@/views/LoggedOutView.vue'),
    meta: { requiresAuth: false },
  },
  {
    path: '/google/callback',
    name: 'GoogleCallback',
    component: () => import('@/views/google/GoogleCallbackView.vue'),
    meta: {
      requiresAuth: false,
      requiredGroups: [],
    },
  },
  {
    path: '/:pathMatch(.*)*',
    redirect: (to) => ({
      path: '/error',
      query: { reason: 'not-found', path: to.path },
    }),
  },
];
